import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../core/client';
import type { RecordRoomLightCalibrationBody, RoomLightCalibration } from './room-light-calibration.types';

// Site-scoped `/v1/*` — cloud prefixes `/sites/{id}` via resolvePath (D-040), so
// the same two calls work from the site console and from a cloud drill-down.
const BASE = '/v1/climate/light-calibrations';

export const roomLightCalibrationKeys = {
  all: ['room-light-calibrations'] as const,
  room: (roomCode: string) => ['room-light-calibrations', roomCode] as const,
};

const enc = encodeURIComponent;

/** One room's light-calibration trail, newest first.
 *
 *  The backend orders by `effective_from DESC` and returns EVERY row including
 *  future-dated ones, so "what is in force now" is `currentFactor(list)`, not
 *  `list[0]`. */
export function useRoomLightCalibration(roomCode: string | null | undefined) {
  return useQuery({
    queryKey: roomLightCalibrationKeys.room(roomCode ?? ''),
    queryFn: () => api.get<RoomLightCalibration[]>(`${BASE}/${enc(roomCode ?? '')}`),
    enabled: Boolean(roomCode),
  });
}

/** Record what a PAR meter measured under this room's fixture.
 *
 *  Invalidates the READINGS as well as the trail: within a publish tick every
 *  PPFD in that room stops being an estimate and changes value, so a cached
 *  snapshot would contradict the dialog that just accepted the number. History is
 *  invalidated for the same reason — the charts re-derive against the factor that
 *  applied at each point, so the curve changes from `effective_from` on. */
export function useRecordRoomLightCalibration() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: RecordRoomLightCalibrationBody) => api.post<RoomLightCalibration>(BASE, body),
    onSuccess: (_d, v) => {
      qc.invalidateQueries({ queryKey: roomLightCalibrationKeys.room(v.room_code) });
      // Every live sensor read is keyed under `iot` (see `iotKeys`); the room's
      // ppfd is derived server-side, so the whole prefix is the honest target.
      qc.invalidateQueries({ queryKey: ['iot'] });
      qc.invalidateQueries({ queryKey: ['history'] });
      // The calibration screen shows each probe's live reading beside its row.
      qc.invalidateQueries({ queryKey: ['calibration-probes'] });
    },
  });
}
